<template>
  <AccordionWithHeader
    :expanded="expanded"
    :expanded-max-height="800"
    title="Invoice history"
    @accordion-click="e => emit('accordion-click', e)">
    <div class="mt-md text-center w-100">
      <div
        v-if="loading"
        class="d-flex justify-content-center">
        <svg-spinner height="40"/>
      </div>
      <div
        v-else-if="invoices.length"
        class="w-100">
        <InvoiceList
          :invoices="viewingInvoices"/>
        <div
          v-if="!isPreviousButtonDisabled || !isNextButtonDisabled"
          class="d-flex justify-content-end">
          <BaseButton 
            class="mt-2"
            variant="tertiary"
            :disabled="isPreviousButtonDisabled"
            @click="onPreviousPage">
            <template #icon>
              <SvgChevronLeft
                height="32"
                width="32"/>
            </template>
          </BaseButton>
          <BaseButton 
            class="mt-2 ms-2"
            variant="tertiary"
            :disabled="isNextButtonDisabled"
            @click="onNextPage">
            <template #icon>
              <SvgChevronRight
                height="32"
                width="32"/>
            </template>
          </BaseButton>
        </div>
      </div>
      <div
        v-else
        class="w-100 px-4 px-md-0 mt-md-4 text-center">
        <div>
          You don't currently have any invoices.
        </div>
      </div>
    </div>
  </AccordionWithHeader>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import AccordionWithHeader from '../global/sequin/AccordionWithHeader.vue'
import InvoiceList from './InvoiceList'
import apiClient from '../../store/api/apiClient'
import SvgSpinner from '@/components/global/svg/SvgSpinner.vue'
import BaseButton from '@shared/components/ADORN/BaseButton'
import SvgChevronLeft from '@/components/global/svg/SvgChevronLeft.vue'
import SvgChevronRight from '@/components/global/svg/SvgChevronRight.vue'


const emit = defineEmits(['accordion-click'])

const loading = ref(true)
const invoices = ref([])
const hasMore = ref(true)
const pageSize = ref(5)
const startIndex = ref(0)
const endIndex = ref(0)

const viewingInvoices = computed(() => {
  return invoices.value.slice(startIndex.value, endIndex.value)
})

const isNextButtonDisabled = computed(() => {
  return endIndex.value === invoices.value.length || invoices.value.length <= pageSize.value - 1
})

const isPreviousButtonDisabled = computed(() => {
  return startIndex.value === 0
})

const onPreviousPage = async () => {
  endIndex.value = Math.max(startIndex.value, 0)
  startIndex.value = Math.max(endIndex.value - pageSize.value, 0)
}

const onNextPage = async () => {
  await fetchInvoices()
  startIndex.value = endIndex.value
  endIndex.value = Math.min(startIndex.value + pageSize.value, invoices.value.length)
}

const fetchInvoices = async () => {
  try {
    if (hasMore.value) {
      loading.value = true
      let res = null
      if (endIndex.value > 0) {
        const startingAfter = invoices.value[invoices.value.length - 1].id
        res = await apiClient.getBillingHistory(startingAfter)
      } else {
        // initial load
        res = await apiClient.getBillingHistory()
        endIndex.value = pageSize.value
      }
      invoices.value.push(...res.data.data)
      hasMore.value = res.data.hasMore
    }
  } catch (err) {
    invoices.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchInvoices()
})
</script>

<style lang="scss" scoped>
  .invoice-table {
    width: 100%;
    border: $default-border;
  }
</style>
